import styled from 'styled-components';
import type { AnalyticsBoxCoordinates } from '../../types/analytics';

interface BoundingBoxProps {
    // Alert history trajectories can include extra values such as timestamps;
    // bounding boxes only consume the first four coordinates.
    $coords: AnalyticsBoxCoordinates;
    $colour: string;
}

export const BoundingBox = styled.div<BoundingBoxProps>`
    position: absolute;
    border-radius: 3px;
    ${({ $coords: [x1, y1, x2, y2], $colour }) => {
        const width = x2 - x1;
        const height = y2 - y1;
        
        // Calculate padding as percentage of box dimensions (e.g., 10% of each dimension)
        const paddingPercent = 0.05; // Adjust this value (0.1 = 10%, 0.05 = 5%, etc.)
        const paddingX = width * paddingPercent;
        const paddingY = height * paddingPercent;
        
        // Add padding while keeping within bounds (0-100%)
        const paddedX1 = Math.max(0, x1 - paddingX);
        const paddedY1 = Math.max(0, y1 - paddingY);
        const paddedX2 = Math.min(100, x2 + paddingX);
        const paddedY2 = Math.min(100, y2 + paddingY);
        
        const paddedWidth = paddedX2 - paddedX1;
        const paddedHeight = paddedY2 - paddedY1;
        
        // Calculate actual pixel dimensions
        const widthPx = paddedWidth * 2;
        const heightPx = paddedHeight * 2;
        
        // Corner size calculation
        const maxCornerSize = 30;
        const minCornerSize = 10;
        const cornerSize = Math.max(
            minCornerSize,
            Math.min(maxCornerSize, Math.min(widthPx, heightPx) * 0.4)
        );
        
        return `
            left: ${paddedX1}%;
            top: ${paddedY1}%;
            width: ${paddedWidth}%;
            height: ${paddedHeight}%;
            
            /* L-shaped corners using gradients */
            background: 
                /* Top-left corner */
                linear-gradient(${$colour}, ${$colour}) 0 0,
                linear-gradient(${$colour}, ${$colour}) 0 0,
                /* Top-right corner */
                linear-gradient(${$colour}, ${$colour}) 100% 0,
                linear-gradient(${$colour}, ${$colour}) 100% 0,
                /* Bottom-left corner */
                linear-gradient(${$colour}, ${$colour}) 0 100%,
                linear-gradient(${$colour}, ${$colour}) 0 100%,
                /* Bottom-right corner */
                linear-gradient(${$colour}, ${$colour}) 100% 100%,
                linear-gradient(${$colour}, ${$colour}) 100% 100%;
            background-repeat: no-repeat;
            background-size: 
                ${cornerSize}px 2px, 2px ${cornerSize}px,
                ${cornerSize}px 2px, 2px ${cornerSize}px,
                ${cornerSize}px 2px, 2px ${cornerSize}px,
                ${cornerSize}px 2px, 2px ${cornerSize}px;
        `;
    }}
`;

BoundingBox.displayName = 'BoundingBox';
